import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { ImagesEditor } from "../features/images/components/ImagesEditor";
import { LinksEditor } from "../features/links/components/LinksEditor";
import { organiserQueries } from "../features/organisers/api/queries";
import { venueQueries } from "../features/venues/api/queries";
import { areaQueries } from "../features/weather/api/queries";
import { AppHeader } from "../shared/ui/AppHeader";

export const Route = createFileRoute(
  "/_authenticated/organisers/$organiserId/",
)({
  loader: async ({ params, context: { queryClient } }) => {
    /*
     * The organiser is awaited and the two reference lists are not: they only
     * name the "usually at" line, so letting them resolve during render costs
     * a label rather than a broken page. The organiser itself must be settled
     * here, because the component cannot render a missing one.
     */
    void queryClient.ensureQueryData(venueQueries.list());
    void queryClient.ensureQueryData(areaQueries.list());

    const data = await queryClient.ensureQueryData(
      organiserQueries.detail(params.organiserId),
    );
    if (!data.organiser) throw notFound();
  },
  component: OrganiserDetailPage,
  errorComponent: ({ error }) => (
    <>
      <AppHeader />
      <main className="mx-auto max-w-2xl p-6">
        <p role="alert" className="text-brand">
          Couldn’t load this organiser: {error.message}
        </p>
      </main>
    </>
  ),
  notFoundComponent: () => (
    <>
      <AppHeader />
      <main className="mx-auto max-w-2xl p-6">
        <h1 className="text-xl font-semibold">Organiser not found</h1>
        <p className="mt-2 text-sm text-content-muted">
          It may have been removed, or the link may be wrong.
        </p>
        <Link
          to="/organisers"
          className="mt-4 inline-block text-sm text-brand underline"
        >
          Back to organisers
        </Link>
      </main>
    </>
  ),
});

/**
 * Group a CVR number for reading: `44129167` becomes `44 12 91 67`.
 *
 * Display only. The stored value stays bare digits, so this never feeds back
 * into a form or a comparison — grouping is how the register prints it and how
 * people read it aloud, not part of the identifier.
 *
 * Anything that is not the expected 8 digits is returned untouched rather than
 * chopped into misleading pairs: rows written before the validator existed are
 * not guaranteed to conform.
 */
function groupCvr(cvr: string): string {
  if (!/^\d{8}$/.test(cvr)) return cvr;
  return cvr.replace(/(\d{2})(?=\d)/g, "$1 ");
}

/**
 * One organiser, with its registry identity and everything attached to it.
 *
 * Read-mostly, unlike the organisers index: name, description and the defaults
 * are edited by the inline form on that page, and duplicating it here would mean
 * two forms writing the same row. What this page adds is somewhere for the
 * registry fields to live — reference-desk detail that would crowd a list — and
 * a stable URL per organiser.
 *
 * The links and images editors *are* here, because both attach to a row that
 * already exists and both save on their own.
 */
function OrganiserDetailPage() {
  const { organiserId } = Route.useParams();
  const { data } = useSuspenseQuery(organiserQueries.detail(organiserId));
  const { data: venueData } = useSuspenseQuery(venueQueries.list());
  const { data: areaData } = useSuspenseQuery(areaQueries.list());

  const organiser = data.organiser;
  // The loader guarantees this; the type does not.
  if (!organiser) return null;

  const venueName = venueData.venues.find(
    (venue) => venue.id === organiser.defaultVenueId,
  )?.name;
  const areaName = areaData.areas.find(
    (area) => area.id === organiser.defaultAreaId,
  )?.name;

  const usualLocation = organiser.defaultVenueId
    ? `Usually at ${venueName ?? "a venue that no longer exists"}`
    : organiser.defaultAreaId
      ? `Usually around ${areaName ?? "an area that no longer exists"}`
      : null;

  /*
   * Whether the registry section has anything in it. Checked up front so the
   * whole block can be dropped rather than rendering a heading over three empty
   * rows — for most organisers all three are null, and an empty "Registration"
   * panel reads as missing data rather than as data that was never expected.
   */
  const hasRegistry = Boolean(
    organiser.cvr ?? organiser.registeredName ?? organiser.foreningsnr,
  );

  const cover =
    organiser.images.find((image) => image.isPrimary) ?? organiser.images[0];

  return (
    <>
      <AppHeader />
      <main className="mx-auto max-w-2xl p-6">
        <Link
          to="/organisers"
          className="text-sm text-content-muted hover:text-content"
        >
          ← All organisers
        </Link>

        <div className="mt-4 flex items-start gap-4">
          {cover && (
            <img
              src={cover.thumbnailUrl}
              alt={cover.altText ?? ""}
              className="h-20 w-20 shrink-0 rounded-lg object-cover"
            />
          )}
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold">{organiser.name}</h1>
            {/*
              Under the display name, in muted type, and only when it actually
              differs. Showing "Salsa Libre / Salsa Libre" would be noise, and the
              comparison is case-insensitive so a registry’s capitalisation does
              not manufacture a difference.
            */}
            {organiser.registeredName &&
              organiser.registeredName.toLowerCase() !==
                organiser.name.toLowerCase() && (
                <p className="text-sm text-content-muted">
                  Registered as {organiser.registeredName}
                </p>
              )}
          </div>
        </div>

        {organiser.description && (
          <p className="mt-4 whitespace-pre-line text-content-muted">
            {organiser.description}
          </p>
        )}

        <dl className="mt-6 grid gap-x-6 gap-y-3 sm:grid-cols-[8rem_1fr]">
          {organiser.contactEmail && (
            <>
              <dt className="text-sm font-medium text-content-muted">
                Contact
              </dt>
              <dd>
                <a
                  href={`mailto:${organiser.contactEmail}`}
                  className="break-all underline hover:text-brand"
                >
                  {organiser.contactEmail}
                </a>
              </dd>
            </>
          )}

          {usualLocation && (
            <>
              <dt className="text-sm font-medium text-content-muted">
                Usual location
              </dt>
              <dd>
                {usualLocation}
                {/*
                  The same caveat the organisers form carries, because this is the
                  other place someone meets these fields and would otherwise read
                  them as where the events are.
                */}
                <span className="block text-sm text-content-muted">
                  A prefill for the event form. It doesn’t move events that
                  already exist.
                </span>
              </dd>
            </>
          )}
        </dl>

        {hasRegistry && (
          <section className="mt-6 rounded-lg border border-border-subtle p-4">
            <h2 className="font-medium">Registration</h2>
            <dl className="mt-3 grid gap-x-6 gap-y-3 sm:grid-cols-[8rem_1fr]">
              {organiser.registeredName && (
                <>
                  <dt className="text-sm font-medium text-content-muted">
                    Registered name
                  </dt>
                  <dd>{organiser.registeredName}</dd>
                </>
              )}

              {organiser.cvr && (
                <>
                  <dt className="text-sm font-medium text-content-muted">
                    CVR
                  </dt>
                  <dd>
                    {/*
                      Links to the official register rather than restating what we
                      hold: a CVR number is only useful if it can be checked, and
                      storing `registeredName` beside it is what makes a wrong
                      match visible without leaving the page.
                    */}
                    <a
                      href={`https://datacvr.virk.dk/enhed/virksomhed/${organiser.cvr}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="underline hover:text-brand"
                    >
                      {groupCvr(organiser.cvr)}
                    </a>
                    <span className="block text-sm text-content-muted">
                      Det Centrale Virksomhedsregister
                    </span>
                  </dd>
                </>
              )}

              {organiser.foreningsnr && (
                <>
                  <dt className="text-sm font-medium text-content-muted">
                    Foreningsnr.
                  </dt>
                  <dd>
                    {organiser.foreningsnr}
                    {/*
                      No link: Københavns Kommune publishes the register as a
                      dated PDF with no per-forening URL, so there is nothing
                      stable to point at. Naming the source is the honest version
                      of a link that would rot.
                    */}
                    <span className="block text-sm text-content-muted">
                      Københavns Kommunes register over godkendte foreninger
                    </span>
                  </dd>
                </>
              )}
            </dl>
          </section>
        )}

        {/*
          Both save on their own and invalidate the organiser queries, so this
          page picks up their changes without a form around them.
        */}
        <div className="mt-6 flex flex-col gap-4">
          <LinksEditor
            owner="organiser"
            ownerId={organiser.id}
            links={organiser.links}
          />
          <ImagesEditor
            owner="organiser"
            ownerId={organiser.id}
            images={organiser.images}
          />
        </div>
      </main>
    </>
  );
}
